import React, { useEffect, useState } from 'react';
import { useBoolean } from 'react-hanger';
import { useApolloClient, useQuery } from '@apollo/client/react';
import { SelectDescriptor } from '@progress/kendo-react-data-tools';
import { GridHeaderSelectionChangeEvent, GridSelectionChangeEvent } from '@progress/kendo-react-grid';
import { Tooltip } from '@progress/kendo-react-tooltip';
import DraggableDialog from '@components-clean/core/draggable-dialog';
import createGridCustomCells, { INamedItem } from '@components-clean/core/grid/custom-cell';
import Grid from '@components-clean/core/grid-fe';
import { GridColumnProps } from '@components-clean/core/grid-fe/grid.interface';
import Loading from '@components-clean/core/loading/loading';
import { useUnsavedChangesContext } from '@contexts-clean/unsaved-changes-context';
import style from '@pages-clean/central-monitoring/monitoring-toolkit/tabs/dialogs/style.module.scss';
import { NamedItemType } from 'types/graphql-global-types';
import { useTranslation } from '@hooks/index';
import Constants from '@utils/constants';
import { notEmpty } from '@utils/utility-helpers';
import { GET_NAMED_ITEMS_BY_TYPE } from '@queries/common';
import {
    getNamedItemsByType,
    getNamedItemsByType_OpraQueries_namedItemsByType_NamedItem as NamedItem,
} from '@queries/generated/common.generated';
import { StudyFields, CriticalVariableFields } from '@queries/generated/fragments.generated';
import {
    getlibraryFunctionalDataReviewsForAssignment_OpraQueries,
    getlibraryFunctionalDataReviewsForAssignmentVariables,
    getlibraryFunctionalDataReviewsForAssignment_OpraQueries_libraryFunctionalDataReviews_LibraryFunctionalDataReview as LibraryFunctionalDataReview,
} from '@queries/generated/risk-assessment.generated';
import {
    GET_LIBRARY_FUNCTIONAL_DATA_REVIEWS_FOR_ASSIGNMENT,
    GET_FUNCTIONAL_DATA_REVIEWS_BY_STUDY_AND_CTQ,
} from '@queries/risk-assessment';

interface AssignFunctionalDataReviewToCtQfProps {
    study: StudyFields;
    onClose: () => void;
    onSubmit: (close: boolean, libraryFunctionalDataReviews: ILibraryFunctionalDataReviewFieldsSubset[]) => void;
    associatedCriticalVariable: CriticalVariableFields;
    updatingFunctionalDataReviews: boolean;
}
export interface ILibraryFunctionalDataReviewFieldsSubset extends Pick<LibraryFunctionalDataReview, 'description'> {
    libraryReferenceId: string;
    criticalToQualityFactorId: string;
    active: boolean;
    studyId: string;
    responsibleFunctionId: string;
}

const COMPONENT_NAME = 'assign-fdrs-to-ctqf-dialog';

const AssignFunctionalDataReviewToCtQfDialog = ({
    study,
    onClose,
    onSubmit,
    associatedCriticalVariable,
    updatingFunctionalDataReviews,
}: AssignFunctionalDataReviewToCtQfProps) => {
    const { confirmDiscardChanges, anyComponentHasChanges, discardChanges, setComponentHasChanges } =
        useUnsavedChangesContext();

    const { t } = useTranslation('ram', { keyPrefix: 'ctqf.ctqFactors.dialogs.assignFunctionalDataReviews' });
    const client = useApolloClient();

    const { TooltipCell, ActiveInactiveCell } = createGridCustomCells();
    const [filteredFunctionalDataReviews, setFilteredFunctionalDataReviews] = useState<
        LibraryFunctionalDataReview[] | null
    >(null);
    const clearSelectedRowsEvent = useBoolean(false);
    const [select, setSelect] = React.useState<SelectDescriptor>({});

    const { data: libraryFunctionalDataReviews, loading: loadingLibraryFunctionalDataReviews } = useQuery<
        getlibraryFunctionalDataReviewsForAssignment_OpraQueries,
        getlibraryFunctionalDataReviewsForAssignmentVariables
    >(GET_LIBRARY_FUNCTIONAL_DATA_REVIEWS_FOR_ASSIGNMENT);

    const { data: responsibleFunctionsData, loading: loadingResponsibleFunctions } = useQuery<getNamedItemsByType>(
        GET_NAMED_ITEMS_BY_TYPE,
        {
            variables: { type: NamedItemType.RESPONSIBLE_FUNCTION },
        },
    );

    const onSubmitAssign = async (close: boolean) => {
        const selectedRows = Object.keys(select).filter((k) => select[k] === true);
        const selectedLibraryFdRs: ILibraryFunctionalDataReviewFieldsSubset[] =
            (filteredFunctionalDataReviews &&
                filteredFunctionalDataReviews
                    .filter((item) => selectedRows.indexOf(item.id) !== -1)
                    .map((fdr) => ({
                        active: fdr.active!,
                        responsibleFunctionId: fdr.responsibleFunction?.id,
                        criticalToQualityFactorId: associatedCriticalVariable.id,
                        description: fdr.description,
                        libraryReferenceId: fdr.id,
                        studyId: study.id,
                    }))) ??
            [];
        setSelect({});
        clearSelectedRowsEvent.setTrue();
        onSubmit(close, selectedLibraryFdRs);
        await client.refetchQueries({ include: [GET_FUNCTIONAL_DATA_REVIEWS_BY_STUDY_AND_CTQ] });
    };

    const getAllFunctionalDataReviews = () => {
        const fdrs = libraryFunctionalDataReviews?.libraryFunctionalDataReviews
            ? libraryFunctionalDataReviews.libraryFunctionalDataReviews
                  .filter(notEmpty)
                  .filter(
                      (libraryFunctionalDataReview: LibraryFunctionalDataReview) =>
                          !associatedCriticalVariable.associatedFdrLibraryIds?.find(
                              (fdrId) => fdrId === libraryFunctionalDataReview.id,
                          ) && libraryFunctionalDataReview.active,
                  )
            : [];
        const sortedFdrs = [...fdrs].sort((a, b) => {
            if (!a.description && b.description) return 1;
            if (!b.description && a.description) return -1;
            if (!a.description && !b.description) return 0;
            return a.description!.localeCompare(b.description!);
        });
        return sortedFdrs as LibraryFunctionalDataReview[];
    };

    useEffect(() => {
        if (
            !loadingResponsibleFunctions &&
            !loadingLibraryFunctionalDataReviews &&
            libraryFunctionalDataReviews?.libraryFunctionalDataReviews
        ) {
            const functionalDataReviews = getAllFunctionalDataReviews();
            // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
            // eslint-disable-next-line @eslint-react/set-state-in-effect
            setFilteredFunctionalDataReviews(functionalDataReviews);
        }
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/exhaustive-deps
    }, [
        libraryFunctionalDataReviews,
        associatedCriticalVariable,
        loadingLibraryFunctionalDataReviews,
        loadingResponsibleFunctions,
    ]);

    const onCloseHandler = async () => {
        if (anyComponentHasChanges()) {
            const confirmed = await confirmDiscardChanges();
            if (!confirmed) {
                return;
            }
            discardChanges();
        }

        onClose();
    };

    const onSelectionChange = React.useCallback((event: GridSelectionChangeEvent) => {
        clearSelectedRowsEvent.setFalse();
        if (event.select) setSelect(event.select);
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/exhaustive-deps
    }, []);

    const onHeaderSelectionChange = React.useCallback((event: GridHeaderSelectionChangeEvent) => {
        clearSelectedRowsEvent.setFalse();
        if (event.select) setSelect(event.select);
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/exhaustive-deps
    }, []);

    const columns: GridColumnProps[] = [
        {
            id: 'checkbox',
            field: 'checkbox',
            columnType: 'checkbox',
            className: 'checkboxSelection',
            width: 40,
            customColumn: true,
            resizable: false,
            filterable: false,
        },
        {
            id: 'description',
            field: 'description',
            title: t('columns.description'),
            minWidth: 415,
            customColumn: true,
            filter: 'text',
            cells: {
                data: TooltipCell({
                    isCsv: false,
                    maxLines: 2,
                }),
            },
        },
        {
            id: 'responsibleFunction',
            field: 'responsibleFunction',
            title: t('columns.responsibleFunction'),
            minWidth: 230,
            customColumn: true,
            filter: 'text',
            filterKey: 'name',
            lovData: responsibleFunctionsData?.namedItemsByType
                ? responsibleFunctionsData.namedItemsByType
                      .filter((ni): ni is NamedItem => ni?.studyId === Constants.ObjectId.Empty)
                      .map((item) => item?.name as string)
                      .sort((a, b) => {
                          return a.localeCompare(b);
                      })
                : [],
            cells: {
                data: ActiveInactiveCell({
                    inactive: (dataItem: INamedItem) => dataItem?.active === false,
                    renderValue: (dataItem: INamedItem) => dataItem?.name,
                }),
            },
        },
        {
            width: 1,
            minWidth: 1,
            id: 'id',
            field: 'id',
            showByDefault: false,
            title: 'ID',
        },
    ];
    const dialogTitle = `${t('title')}`;
    const formActionsEnabled =
        select && Object.keys(select).filter((k) => select[k] === true).length > 0 && !updatingFunctionalDataReviews;
    const description = associatedCriticalVariable.description ?? '';

    useEffect(() => {
        setComponentHasChanges(COMPONENT_NAME, formActionsEnabled);
    }, [formActionsEnabled, setComponentHasChanges]);

    if (loadingLibraryFunctionalDataReviews || loadingResponsibleFunctions) return <Loading />;

    return (
        <DraggableDialog
            className={style.kWindowContent}
            dataTestId="assign-risks-to-ctq-factor-dialog"
            title={dialogTitle}
            onClose={onCloseHandler}
            onSubmit={() => onSubmitAssign(false)}
            primaryBtnLabel={t('buttons.assign')}
            primaryBtnDisabled={!formActionsEnabled}
            secondaryBtnLabel={t('buttons.close')}
            onAdditionalAction={() => onSubmitAssign(true)}
            additionalBtnLabel={t('buttons.assignAndClose')}
            additionalBtnDisabled={!formActionsEnabled}
            initialHeight={420}
            initialWidth={722}
            height={null}
        >
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }} className="k-w-full k-h-full">
                <div>
                    {description.length <= 600 ? (
                        <>
                            <span className="k-font-medium">{t('descriptionLabel')} - </span>{' '}
                            <span className={style.textcolor}>{description}</span>
                        </>
                    ) : (
                        <Tooltip anchorElement="target" position="auto" parentTitle style={{ maxWidth: '600px' }}>
                            <span className="k-font-medium">{t('descriptionLabel')} - </span>{' '}
                            <span title={description}>{`${description.slice(0, 600)}...`}</span>
                        </Tooltip>
                    )}
                </div>
                <div style={{ height: '300px', paddingBottom: '0.25rem' }}>
                    <Grid
                        excludeHiddenRowMenuActions
                        title={t('title')}
                        maxHeight="auto"
                        testDataId="ctqf-fdrs-grid"
                        data={filteredFunctionalDataReviews}
                        columns={columns}
                        filterable
                        loading={loadingLibraryFunctionalDataReviews || updatingFunctionalDataReviews}
                        useLoadingOverlay
                        showAddIcon={false}
                        scrollable="virtual"
                        dataItemKey="id"
                        selectable={{
                            enabled: true,
                            drag: false,
                            cell: false,
                            mode: 'multiple',
                        }}
                        onHeaderSelectionChange={onHeaderSelectionChange}
                        onSelectionChange={onSelectionChange}
                        clearGridSelectedRows={clearSelectedRowsEvent.value}
                        useCardWrapper={false}
                        onFilterChange={async () => {
                            clearSelectedRowsEvent.setTrue();
                            setSelect({});
                        }}
                    />
                </div>
            </div>
        </DraggableDialog>
    );
};

export default AssignFunctionalDataReviewToCtQfDialog;
